import { CustomFetcher, MapOptionsToKeyFunction } from 'fetchye';
import { RootStateOrAny } from 'react-redux';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { PWP_ERROR_CODE } from '../constants/apiConstants';
import { Membership } from '../types/data.types';

type CustomFetcherOptions = {
    mapOptionsToKey?: MapOptionsToKeyFunction<object>
    mapKeyToCacheKey?: (key: string) => string;
    headers: {
        [key: string]: string;
    };
    defer?: boolean;
};

interface CustomError extends Error {
    message: string;
    locations: [];
    extensions: {
        errorCode: string;
        errorDesc: string;
        classification: string;
    }
}

type GraphQLResponse = {
    data: Membership;
    errors: CustomError[];
};

export const graphqlFetcher = (
  dispatch: RootStateOrAny,
  membershipDomainApiUrl: string,
  fetcherOpts: {
    reporting: {
      moduleID: string;
      fetch: {
        url: string;
        activeLocale: string;
      };
    };
  }
): CustomFetcher<CustomFetcherOptions, Membership> => async (
  fetchClient, key, options
) => {
  let res: Response | undefined;
  let payload;
  let error: CustomError | Error | undefined;

  try {
    res = await fetchClient(membershipDomainApiUrl, {
      ...options,
      body: JSON.stringify(key),
    });

    const { data, errors } = (await res.json()) as GraphQLResponse;

    payload = data;
    const errorsNotExpected = errors?.filter((x) => x?.extensions?.errorCode !== PWP_ERROR_CODE);

    if (errorsNotExpected?.length > 0) {
      errorsNotExpected?.forEach((err) => {
        const reportedError = new Error(err.message);
        dispatch(addErrorToReport(
          reportedError,
          {
            collectionMethod: 'GraphQL Fetch Error - Payload',
            errorCode: err.extensions?.errorCode,
            errorDescription: err.extensions?.errorDesc,
            correlationId: options?.headers['X-Correlation-Id'],
            ...fetcherOpts.reporting,
          }
        ));
      });
    }
  } catch (requestError) {
    // eslint-disable-next-line no-console -- error useful to developer in specific error case
    console.error(requestError);

    error = requestError as Error;
    dispatch(addErrorToReport(
      error,
      {
        reqCorrelationId: options?.headers['X-Correlation-Id'],
        collectionMethod: 'GraphQL Fetch Error - Request',
        ...fetcherOpts.reporting,
      }
    ));
  }

  return {
    payload,
    error,
    ok: res?.ok,
    status: res?.status,
  };
};
